import type { ImportOrderStatus } from '../types/import-tracking.types';

/**
 * Qué pestañas del detalle de orden están disponibles según su estado.
 *
 * Antes las ocho se mostraban desde el minuto cero, y al crear una cotización
 * la mayoría quedaban vacías sin explicar por qué. Eso era una de las quejas
 * concretas: "muchas pestañas vacías sin explicación".
 *
 * Ahora aparecen a medida que el embarque avanza, y las que todavía no se
 * pueden usar se muestran atenuadas con el motivo — no se ocultan, así el
 * usuario sabe que existen y cuándo las va a tener.
 *
 * Puro a propósito: la regla de negocio se testea sin montar la pantalla.
 */

/*
  "Entidades" ya no está.

  Era la pestaña del consignatario, el notificado y el despachante. Se sacó
  porque **nadie leía esos datos**: `importOrderParty` no aparece en ningún
  lado del backend fuera de su propio CRUD, y el generador de HBL/AWB —el
  único que los necesitaría— saca el consignatario de `order.customer` y deja
  el shipper en `null`. Se podían cargar las entidades y el documento salía
  igual de vacío. Cero filas cargadas en la base.

  La tabla y sus endpoints se dejan: si algún día el HBL usa estos datos, la
  pestaña vuelve. Lo que se quita es una pantalla que pedía trabajo y no lo
  devolvía a ningún lado.
*/
export type OrderTabKey =
  | 'general'
  | 'products'
  | 'costs'
  | 'containers'
  | 'documents'
  | 'tracking'
  | 'history';

/** En qué momento del ciclo se habilita cada pestaña. */
type Stage = 'always' | 'ordered' | 'shipped';

const TAB_STAGE: Record<OrderTabKey, Stage> = {
  // Se puede trabajar desde que es una cotización.
  general: 'always',
  products: 'always',
  costs: 'always',
  history: 'always',

  // Recién cuando dejó de ser cotización tiene sentido cargar las partes y
  // armar los contenedores: antes no hay a quién declarar ni qué estibar.
  containers: 'ordered',

  // Los documentos y el seguimiento existen cuando la carga se movió.
  documents: 'shipped',
  tracking: 'shipped',
};

/**
 * Estados en los que ya es una orden y no una cotización.
 *
 * Incluye `DRAFT` a propósito: `convert-to-order` deja la orden justamente en
 * `DRAFT`, así que exigir `CONFIRMED` dejaba las pestañas trabadas después de
 * convertir — el usuario apretaba "Convertir a orden" y no se habilitaba nada.
 * La regla real es "ya no es cotización", no "está confirmada".
 */
const ORDERED_ONWARD: ImportOrderStatus[] = [
  'DRAFT',
  'CONFIRMED',
  'SHIPPED',
  'IN_TRANSIT',
  'CUSTOMS',
  'CUSTOMS_HOLD',
  'DELIVERED',
];

/** Estados en los que la carga ya zarpó. */
const SHIPPED_ONWARD: ImportOrderStatus[] = [
  'SHIPPED',
  'IN_TRANSIT',
  'CUSTOMS',
  'CUSTOMS_HOLD',
  'DELIVERED',
];

export interface TabAvailability {
  enabled: boolean;
  /** Por qué está atenuada. `null` si está disponible. */
  reason: string | null;
}

/**
 * ¿Se puede usar esta pestaña con este estado?
 *
 * Una orden `CANCELLED` conserva todo habilitado: hay que poder consultar lo
 * que pasó, aunque no se pueda seguir operando.
 */
export function tabAvailability(tab: OrderTabKey, status: ImportOrderStatus): TabAvailability {
  if (status === 'CANCELLED') return { enabled: true, reason: null };

  const stage = TAB_STAGE[tab];
  if (stage === 'always') return { enabled: true, reason: null };

  if (stage === 'ordered') {
    return ORDERED_ONWARD.includes(status)
      ? { enabled: true, reason: null }
      : { enabled: false, reason: 'Se habilita al convertir la cotización en orden' };
  }

  return SHIPPED_ONWARD.includes(status)
    ? { enabled: true, reason: null }
    : { enabled: false, reason: 'Se habilita cuando la carga zarpe' };
}

/** Las pestañas habilitadas, en el orden en que se muestran. */
export const TAB_ORDER: OrderTabKey[] = [
  'general',
  'products',
  'costs',
  'containers',
  'documents',
  'tracking',
  'history',
];

export function enabledTabs(status: ImportOrderStatus): OrderTabKey[] {
  return TAB_ORDER.filter((tab) => tabAvailability(tab, status).enabled);
}

/**
 * Pestaña a la que caer si la actual dejó de estar disponible.
 *
 * Pasa cuando alguien llega por un enlace a `?tab=documentos` de una orden que
 * todavía es cotización: en vez de mostrar una pestaña vacía, se cae a General.
 */
export function resolveTab(requested: string | null, status: ImportOrderStatus): OrderTabKey {
  const candidate = TAB_ORDER.find((tab) => tab === requested);
  if (candidate && tabAvailability(candidate, status).enabled) return candidate;
  return 'general';
}
